import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useSettings } from '../../contexts/SettingsContext';
import { apiService } from '../../services/apiService';
import { useNavigate, useParams } from 'react-router-dom';
import { X, Printer } from 'lucide-react';
import ReactDOM from 'react-dom';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';

interface PurchaseInvoice {
  _id: string;
  invoiceNumber: string;
  date?: string;
  customer?: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
    contactPerson?: string;
  };
  customerName?: string;
  customerAddress?: string;
  contactPerson?: string;
  workOrder?: {
    number?: string;
    date?: string;
  };
  deliveryAddress?: string;
  items: any[];
  status: string;
  createdAt: string;
  total: number;
}

interface PrintViewProps {
  invoice: PurchaseInvoice;
  settings: any;
  currentPage: number;
  itemsPerPage: number;
  printMode?: boolean;
}

const PurchaseInvoicePrintView: React.FC<PrintViewProps> = ({ invoice, settings, currentPage, itemsPerPage, printMode }) => {
  const { settings: globalSettings } = useSettings();
  const purchaseSettings = globalSettings?.purchaseInvoice || globalSettings?.invoice || {};
  const company = globalSettings?.company || {};
  const itemsPerPagePrint = 14;
  const printTime = new Date().toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'medium' });
  const totalPages = Math.ceil((invoice.items?.length || 0) / itemsPerPagePrint) || 1;

  // Style definitions matching delivery invoice
  const customerNameStyle: React.CSSProperties = { fontWeight: 'bold', fontSize: '11.5pt' } as any;
  const customerAddressStyle: React.CSSProperties = { fontSize: '11.5pt' } as any;
  const customerPhoneStyle: React.CSSProperties = { fontSize: '11.5pt' } as any;
  const customerEmailStyle: React.CSSProperties = { fontSize: '11.5pt' } as any;
  const projectInfoStyle: React.CSSProperties = { fontWeight: 'bold', borderTop: '1px solid #000', borderBottom: '1px solid #000', margin: '16px 0 4px 0', fontSize: '11.5pt', paddingTop: 4 } as any;
  const projectNameStyle: React.CSSProperties = { fontWeight: 'bold', fontSize: '11.5pt' } as any;
  const deliveryAddressStyle: React.CSSProperties = { fontSize: '11.5pt' } as any;

  // Add variables for all style objects used in the JSX
  const invoiceHeaderRowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', margin: 0, width: 'auto', padding: 0 } as any;
  const logoContainerStyle: React.CSSProperties = { flex: '0 0 auto', paddingLeft: 5, paddingTop: 32, height: '50px', display: 'flex', alignItems: 'center', maxHeight: '50px', overflow: 'visible' } as any;
  const companyInfoStyle: React.CSSProperties = { flex: '1 1 auto', textAlign: 'right', marginLeft: 16, paddingRight: 5, paddingTop: 32 } as any;
  const companyInfoTextStyle: React.CSSProperties = { fontSize: '7.5pt' } as any;
  const invoiceTitleStyle: React.CSSProperties = { textAlign: 'center', fontWeight: 'bold', marginBottom: 4 } as any;
  const invoiceDetailsRowStyle: React.CSSProperties = { display: 'flex', border: '1px solid #000', borderBottom: 'none' } as any;
  const customerDetailsColStyle: React.CSSProperties = { flex: 1, borderRight: '1px solid #000', padding: '8px' } as any;
  const deliveryDetailsColStyle: React.CSSProperties = { flex: 1, padding: '8px' } as any;
  const customerInfoTitleStyle: React.CSSProperties = { fontWeight: 'bold', borderBottom: '1px solid #000', marginBottom: 4, fontSize: '11.5pt' } as any;
  const deliveryInfoTitleStyle: React.CSSProperties = { fontWeight: 'bold', borderBottom: '1px solid #000', marginBottom: 4, fontSize: '11.5pt' } as any;
  const deliveryInfoRowStyle: React.CSSProperties = { display: 'flex' } as any;
  const deliveryInfoLabelStyle: React.CSSProperties = { flex: 1, fontWeight: 'bold', fontSize: '11.5pt' } as any;
  const deliveryInfoValueStyle: React.CSSProperties = { flex: 1, fontSize: '11.5pt' } as any;
  const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginBottom: 0 } as any;
  const thSLStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', width: '32px', textAlign: 'center', fontSize: '11.5pt' } as any;
  const thDescStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', textAlign: 'left' } as any;
  const thQtyStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', width: '60px', textAlign: 'center', fontSize: '11.5pt' } as any;
  const thUnitStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', width: '60px', textAlign: 'center', fontSize: '11.5pt' } as any;
  const trStyle: React.CSSProperties = { verticalAlign: 'top' } as any;
  const tdSLStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', textAlign: 'center', verticalAlign: 'top', fontSize: '11.5pt' } as any;
  const tdDescStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', verticalAlign: 'top', whiteSpace: 'pre-line', fontSize: '11pt' } as any;
  const tdQtyStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', textAlign: 'center', verticalAlign: 'top', fontSize: '11.5pt' } as any;
  const tdUnitStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', textAlign: 'center', verticalAlign: 'top', fontSize: '11.5pt' } as any;
  const summaryRowStyle: React.CSSProperties = { display: 'flex', border: '1px solid #000', borderTop: 'none', alignItems: 'center', minHeight: '20px' } as any;
  const summaryColStyle: React.CSSProperties = { flex: 1, padding: '1.5pt 3pt', borderRight: '1px solid #000', fontWeight: 'bold', fontSize: '8.25pt', lineHeight: '1.1' } as any;
  const summaryColRightStyle: React.CSSProperties = { flex: 1, textAlign: 'right', padding: '1.5pt 3pt', fontWeight: 'bold', fontSize: '8.25pt', lineHeight: '1.1' } as any;
  const bottomSectionStyle: React.CSSProperties = { pageBreakInside: 'avoid', breakInside: 'avoid' } as any;
  const bottomTableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginTop: 0, background: '#fff' } as any;
  const bottomTdLeftStyle: React.CSSProperties = { verticalAlign: 'top', textAlign: 'left', fontSize: '10.5pt', width: '40%', padding: '0 6pt' } as any;
  const bottomTdQRStyle: React.CSSProperties = { borderBottom: '1px solid #000', borderLeft: 'none', borderLeftWidth: 0, verticalAlign: 'middle', textAlign: 'center', width: '70px', minWidth: '70px', maxWidth: '70px', padding: 0 } as any;
  const qrContainerStyle: any = { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', padding: 0, margin: 0 };
  const qrStyle: React.CSSProperties = { width: '60%', height: '60%', display: 'block', margin: 0, padding: 0 } as any;
  const bottomTdRightStyle: React.CSSProperties = { verticalAlign: 'top', textAlign: 'right', fontSize: '10.5pt', width: '40%', padding: '0 6pt' } as any;
  const bottomTrStyle: React.CSSProperties = { minHeight: 32 } as any;
  const bottomTdSignStyle: React.CSSProperties = { fontWeight: 'bold', fontSize: '10.875pt', padding: '0 6pt 6pt 6pt', verticalAlign: 'bottom' } as any;
  const bottomTdSignRightStyle: React.CSSProperties = { fontWeight: 'bold', fontSize: '10.875pt', padding: '0 6pt 6pt 6pt', textAlign: 'right', verticalAlign: 'bottom' } as any;
  const bottomFooterStyle: React.CSSProperties = { fontSize: '9pt', color: '#222', marginTop: 8, display: 'flex', justifyContent: 'space-between', width: '100%' } as any;
  const printTimeStyle: React.CSSProperties = { textAlign: 'right' } as any;

  if (printMode) {
    // Split items into pages for print
    const pages = [];
    for (let i = 0; i < invoice.items.length; i += itemsPerPagePrint) {
      pages.push(invoice.items.slice(i, i + itemsPerPagePrint));
    }
    return (
      <div
        className="invoice-container"
        style={{
          margin: '24px auto',
          maxWidth: '210mm',
          background: '#fff',
          padding: 0,
        } as any}
      >
        <style>{`
          @page { margin: 0; }
          /* @page removed for Safari compatibility */
          .invoice-row { display: none; }
          @media screen { .invoice-row.visible { display: table-row; } }
          @media print { .invoice-row { display: table-row !important; } .print:hidden { display: none !important; } }
          .invoice-bottom-section { page-break-inside: avoid; break-inside: avoid; }
          .print-invoice-area, .print-invoice-area * {
            font-size: 9pt !important;
          }
          .invoice-title {
            font-size: 14pt !important;
            font-weight: bold !important;
          }
          .invoice-logo {
            height: 30px !important;
            width: auto !important;
            max-height: 30px !important;
          }
          @media print {
            @page { size: A4 portrait; margin: 10mm; }
            body, html {
              width: 210mm;
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            .print-invoice-area, .print-invoice-area * {
              font-size: 9pt !important;
            }
            .invoice-title {
              font-size: 14pt !important;
              font-weight: bold !important;
            }
            .invoice-header-row { margin: 0 !important; padding: 0 !important; }
            .invoice-header-row > div { margin: 0 !important; padding-top: 0 !important; }
            .invoice-logo {
              height: 30px !important;
              width: auto !important;
              max-height: 30px !important;
            }
          }
          @media print {
            .invoice-container {
              margin: 0 auto 10mm auto !important;
              max-width: 210mm !important;
              background: #fff !important;
              padding: 0 !important;
              border: none !important;
              box-shadow: none !important;
            }
          }
          
          /* Mobile-specific print styles */
          @media print and (max-width: 768px) {
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            body, html {
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              box-sizing: border-box !important;
            }
            
            .invoice-container {
              width: 100% !important;
              display: block !important;
              position: relative !important;
              overflow: visible !important;
              height: auto !important;
              min-height: auto !important;
              max-height: none !important;
              transform: none !important;
              opacity: 1 !important;
              z-index: 9999 !important;
              margin: 0 !important;
              background: #fff !important;
              padding: 0 !important;
              border: none !important;
              box-shadow: none !important;
            }
            
            .invoice-container * {
              visibility: visible !important;
            }
            
            .print-invoice-area, .print-invoice-area * {
              font-size: 8pt !important;
              line-height: 1.2 !important;
              color: #000 !important;
              background: transparent !important;
            }
            
            .invoice-title {
              font-size: 12pt !important;
              font-weight: bold !important;
            }
            
            .invoice-header-row { margin: 0 !important; padding: 0 !important; }
            .invoice-header-row > div { margin: 0 !important; padding-top: 0 !important; }
            .invoice-logo {
              height: 30px !important;
              width: auto !important;
              max-height: 30px !important;
            }
          }
        `}</style>
        {pages.map((pageItems, pageIndex) => (
          <div
            key={pageIndex}
            className="bg-white"
            id={printMode ? `purchase-invoice-print-root-${pageIndex}` : undefined}
            style={{
              pageBreakInside: 'avoid',
              breakInside: 'avoid',
              maxWidth: '210mm',
              margin: '0 auto',
              background: '#fff',
              padding: '20px',
              maxHeight: '80vh',
              overflowY: 'auto',
            } as React.CSSProperties}
          >
            {/* Header Row */}
            <div className="invoice-header-row" style={invoiceHeaderRowStyle}>
              <div style={logoContainerStyle}>
                {settings?.company?.logo ? (
                  <img
                    src={`${process.env.REACT_APP_API_URL || 'https://aircons-inventory-backend.onrender.com'}${settings.company.logo}`}
                    alt="Invoice Logo"
                    className="invoice-logo"
                    style={{ height: '30px', width: 'auto', maxHeight: '30px', objectFit: 'contain' } as React.CSSProperties}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/The Aircons Ltd. Logo Org.png";
                    }}
                  />
                ) : (
                  <img
                    src="/The Aircons Ltd. Logo Org.png"
                    alt="Invoice Logo"
                    className="invoice-logo"
                    style={{ height: '30px', width: 'auto', maxHeight: '30px', objectFit: 'contain' } as React.CSSProperties}
                  />
                )}
              </div>
              <div style={companyInfoStyle}>
                <div style={companyInfoTextStyle}>
                  <div style={customerAddressStyle}>{(company as any).address || 'Plot-14, Main Road, Block-C, Banasree, Rampura, Dhaka-1219'}</div>
                  <div style={customerPhoneStyle}>Phone: {(company as any).phone || '+880 1749-121607'} | Email: {(company as any).email || 'support@theaircons.com'}</div>
                  <div style={customerEmailStyle}>{(company as any).website || 'https://www.theaircons.com'}</div>
                </div>
              </div>
            </div>
            
            {/* Invoice Title */}
            <div style={invoiceTitleStyle}>Purchase Invoice</div>
            
            {/* Invoice Details Row */}
            <div style={invoiceDetailsRowStyle}>
              <div style={customerDetailsColStyle}>
                <div style={customerInfoTitleStyle}>Supplier Details</div>
                <div style={customerNameStyle}>{invoice.customer?.name || invoice.customerName || ''}</div>
                <div style={customerAddressStyle}>{invoice.customer?.address || invoice.customerAddress || ''}</div>
                <div style={customerPhoneStyle}>Phone: {invoice.customer?.phone || ''}</div>
                <div style={customerPhoneStyle}>Contact: {invoice.customer?.contactPerson || invoice.contactPerson || ''}</div>
                <div style={customerEmailStyle}>Email: {invoice.customer?.email || ''}</div>
              </div>
              <div style={deliveryDetailsColStyle}>
                <div style={deliveryInfoTitleStyle}>Purchase Invoice Details</div>
                <div style={deliveryInfoRowStyle}>
                  <div style={deliveryInfoLabelStyle}>Purchase Invoice No.</div>
                  <div style={deliveryInfoValueStyle}>{invoice.invoiceNumber}</div>
                </div>
                <div style={deliveryInfoRowStyle}>
                  <div style={deliveryInfoLabelStyle}>Date</div>
                  <div style={deliveryInfoValueStyle}>{invoice.date ? new Date(invoice.date).toLocaleDateString() : (invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString() : '')}</div>
                </div>
                <div style={deliveryInfoRowStyle}>
                  <div style={deliveryInfoLabelStyle}>Work Order No.</div>
                  <div style={deliveryInfoValueStyle}>{invoice.workOrder?.number || ''}</div>
                </div>
                <div style={deliveryInfoRowStyle}>
                  <div style={deliveryInfoLabelStyle}>Delivery Address</div>
                  <div style={deliveryInfoValueStyle}>{invoice.deliveryAddress || ''}</div>
                </div>
              </div>
            </div>
            
            {/* Items Table */}
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thSLStyle}>SL</th>
                  <th style={thDescStyle}>Description of Goods</th>
                  <th style={thQtyStyle}>Quantity</th>
                  <th style={thUnitStyle}>Unit</th>
                </tr>
              </thead>
              <tbody>
                {pageItems && pageItems.length > 0 ? (
                  pageItems.map((item, idx) => (
                    <tr key={idx} style={trStyle}>
                      <td style={tdSLStyle}>{pageIndex * itemsPerPagePrint + idx + 1}</td>
                      <td style={tdDescStyle}>
                        <div style={{ whiteSpace: 'pre-line' } as React.CSSProperties}>
                          <div><b>Product Name:</b> {item.name}</div>
                          {item.brand && <div><b>Brand:</b> {item.brand}</div>}
                          {item.model && <div><b>Model:</b> {item.model}</div>}
                          {item.sku && <div><b>SKU:</b> {item.sku}</div>}
                          {item.type && <div><b>Type:</b> {item.type}</div>}
                          {item.compressorType && <div><b>Compressor Type:</b> {item.compressorType}</div>}
                          {item.countryOfOrigin && <div><b>Country of Origin:</b> {item.countryOfOrigin}</div>}
                          {item.countryOfAssembly && <div><b>Country of Assembly:</b> {item.countryOfAssembly}</div>}
                          {item.details && <div><b>Details:</b> {item.details}</div>}
                          {item.description && <div><b>Description:</b> {item.description}</div>}
                          {item.remarks && <div><b>Note:</b> {item.remarks}</div>}
                        </div>
                      </td>
                      <td style={tdQtyStyle}>{item.quantity ?? item.qty}</td>
                      <td style={tdUnitStyle}>{item.unit}</td>
                    </tr>
                  ))
                ) : (
                  <tr style={trStyle}>
                    <td colSpan={4} style={tdSLStyle}>No items.</td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {/* Summary Row */}
            <div style={summaryRowStyle}>
              <div style={summaryColStyle}>Total {invoice.items?.length || 0} Items</div>
              <div style={summaryColRightStyle}>E. & O.E</div>
            </div>
            
            {/* Bottom Section */}
            {printMode && pageIndex === pages.length - 1 && (
              <div style={bottomSectionStyle}>
                <table style={bottomTableStyle}>
                  <tbody>
                    <tr style={bottomTrStyle}>
                      <td style={bottomTdLeftStyle}></td>
                      <td style={bottomTdQRStyle}>
                        <div style={qrContainerStyle}>
                          <QRCodeSVG 
                            value={`https://aircons-inventory-backend.onrender.com/api/invoices/purchase-invoices/${invoice._id}/pdf`} 
                            style={qrStyle}
                          />
                        </div>
                      </td>
                      <td style={bottomTdRightStyle}>E. & O.E<br />for {(company as any).name || 'The Aircons Ltd.'}</td>
                    </tr>
                    <tr style={bottomTrStyle}>
                      <td style={bottomTdSignStyle}>Supplier's Signature</td>
                      <td style={bottomTdQRStyle}></td>
                      <td style={bottomTdSignRightStyle}>Authorised Signatory</td>
                    </tr>
                   </tbody>
                 </table>
                 
                 {/* Footer */}
                 <div style={bottomFooterStyle}>
                   <div>This is a computer-generated document.</div>
                   <div style={printTimeStyle}>Print Time: {printTime}</div>
                 </div>
               </div>
             )}
           </div>
         ))}
       </div>
     );
   }

  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const pageItems = invoice.items?.slice(startIdx, endIdx) || [];

  return (
    <div
      className="bg-white p-8 rounded shadow print-invoice-container font-serif text-black max-w-[900px] mx-auto mt-8 h-auto"
      style={{
        margin: '24px auto',
        maxWidth: '210mm',
        background: '#fff',
        padding: '20px',
        maxHeight: '80vh',
        overflowY: 'auto',
      } as React.CSSProperties}
    >
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          .print-invoice-container {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            min-height: unset !important;
            height: auto !important;
            overflow: visible !important;
            break-after: avoid !important;
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
            background: #fff !important;
          }
        }
        @media screen {
          .print-invoice-container {
            width: 210mm !important;
            max-width: 210mm !important;
            min-height: 297mm !important;
            background: #fff !important;
            margin: 0 auto !important;
            padding: 20mm !important;
            box-shadow: 0 0 10px rgba(0,0,0,0.1) !important;
          }
        }
      `}</style>
      
      {/* Header Row */}
      <div className="invoice-header-row" style={invoiceHeaderRowStyle}>
        <div style={logoContainerStyle}>
          {settings?.company?.logo ? (
            <img
              src={`${process.env.REACT_APP_API_URL || 'https://aircons-inventory-backend.onrender.com'}${settings.company.logo}`}
              alt="Invoice Logo"
              className="invoice-logo"
              style={{ height: '30px', width: 'auto', maxHeight: '30px', objectFit: 'contain' } as React.CSSProperties}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "/The Aircons Ltd. Logo Org.png";
              }}
            />
          ) : (
            <img
              src="/The Aircons Ltd. Logo Org.png"
              alt="Invoice Logo"
              className="invoice-logo"
              style={{ height: '30px', width: 'auto', maxHeight: '30px', objectFit: 'contain' } as React.CSSProperties}
            />
          )}
        </div>
        <div style={companyInfoStyle}>
          <div style={companyInfoTextStyle}>
            <div style={customerAddressStyle}>{(company as any).address || 'Plot-14, Main Road, Block-C, Banasree, Rampura, Dhaka-1219'}</div>
            <div style={customerPhoneStyle}>Phone: {(company as any).phone || '+880 1749-121607'} | Email: {(company as any).email || 'support@theaircons.com'}</div>
            <div style={customerEmailStyle}>{(company as any).website || 'https://www.theaircons.com'}</div>
          </div>
        </div>
      </div>
      
      {/* Invoice Title */}
      <div style={invoiceTitleStyle}>Purchase Invoice</div>
      
      {/* Invoice Details Row */}
      <div style={invoiceDetailsRowStyle}>
        <div style={customerDetailsColStyle}>
          <div style={customerInfoTitleStyle}>Supplier Details</div>
          <div style={customerNameStyle}>{invoice.customer?.name || invoice.customerName || ''}</div>
          <div style={customerAddressStyle}>{invoice.customer?.address || invoice.customerAddress || ''}</div>
          <div style={customerPhoneStyle}>Phone: {invoice.customer?.phone || ''}</div>
          <div style={customerPhoneStyle}>Contact: {invoice.customer?.contactPerson || invoice.contactPerson || ''}</div>
          <div style={customerEmailStyle}>Email: {invoice.customer?.email || ''}</div>
        </div>
        <div style={deliveryDetailsColStyle}>
          <div style={deliveryInfoTitleStyle}>Purchase Invoice Details</div>
          <div style={deliveryInfoRowStyle}>
            <div style={deliveryInfoLabelStyle}>Purchase Invoice No.</div>
            <div style={deliveryInfoValueStyle}>{invoice.invoiceNumber}</div>
          </div>
          <div style={deliveryInfoRowStyle}>
            <div style={deliveryInfoLabelStyle}>Date</div>
            <div style={deliveryInfoValueStyle}>{invoice.date ? new Date(invoice.date).toLocaleDateString() : (invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString() : '')}</div>
          </div>
          <div style={deliveryInfoRowStyle}>
            <div style={deliveryInfoLabelStyle}>Work Order No.</div>
            <div style={deliveryInfoValueStyle}>{invoice.workOrder?.number || ''}</div>
          </div>
          <div style={deliveryInfoRowStyle}>
            <div style={deliveryInfoLabelStyle}>Delivery Address</div>
            <div style={deliveryInfoValueStyle}>{invoice.deliveryAddress || ''}</div>
          </div>
        </div>
      </div>
      
      {/* Items Table */}
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thSLStyle}>SL</th>
            <th style={thDescStyle}>Description of Goods</th>
            <th style={thQtyStyle}>Quantity</th>
            <th style={thUnitStyle}>Unit</th>
          </tr>
        </thead>
        <tbody>
          {pageItems && pageItems.length > 0 ? (
            pageItems.map((item, idx) => (
              <tr key={idx} style={trStyle}>
                <td style={tdSLStyle}>{startIdx + idx + 1}</td>
                <td style={tdDescStyle}>
                  <div style={{ whiteSpace: 'pre-line' } as React.CSSProperties}>
                    <div><b>Product Name:</b> {item.name}</div>
                    {item.brand && <div><b>Brand:</b> {item.brand}</div>}
                    {item.model && <div><b>Model:</b> {item.model}</div>}
                    {item.sku && <div><b>SKU:</b> {item.sku}</div>}
                    {item.type && <div><b>Type:</b> {item.type}</div>}
                    {item.compressorType && <div><b>Compressor Type:</b> {item.compressorType}</div>}
                    {item.countryOfOrigin && <div><b>Country of Origin:</b> {item.countryOfOrigin}</div>}
                    {item.countryOfAssembly && <div><b>Country of Assembly:</b> {item.countryOfAssembly}</div>}
                    {item.details && <div><b>Details:</b> {item.details}</div>}
                    {item.description && <div><b>Description:</b> {item.description}</div>}
                    {item.remarks && <div><b>Note:</b> {item.remarks}</div>}
                  </div>
                </td>
                <td style={tdQtyStyle}>{item.quantity ?? item.qty}</td>
                <td style={tdUnitStyle}>{item.unit}</td>
              </tr>
            ))
          ) : (
            <tr style={trStyle}>
              <td colSpan={4} style={tdSLStyle}>No items.</td>
            </tr>
          )}
        </tbody>
      </table>
      
      {/* Summary Row */}
      <div style={summaryRowStyle}>
        <div style={summaryColStyle}>Total {invoice.items?.length || 0} Items</div>
        <div style={summaryColRightStyle}>E. & O.E</div>
      </div>
      
      {/* Bottom Section */}
      <div style={bottomSectionStyle}>
        <table style={bottomTableStyle}>
          <tbody>
            <tr style={bottomTrStyle}>
              <td style={bottomTdLeftStyle}></td>
              <td style={bottomTdQRStyle}>
                <div style={qrContainerStyle}>
                  <QRCodeSVG 
                    value={`https://aircons-inventory-backend.onrender.com/api/invoices/purchase-invoices/${invoice._id}/pdf`} 
                    style={qrStyle}
                  />
                </div>
              </td>
              <td style={bottomTdRightStyle}>E. & O.E<br />for {(company as any).name || 'The Aircons Ltd.'}</td>
            </tr>
            <tr style={bottomTrStyle}>
              <td style={bottomTdSignStyle}>Supplier's Signature</td>
              <td style={bottomTdQRStyle}></td>
              <td style={bottomTdSignRightStyle}>Authorised Signatory</td>
            </tr>
           </tbody>
         </table>
         
         {/* Footer */}
         <div style={bottomFooterStyle}>
           <div>This is a computer-generated document.</div>
           <div style={printTimeStyle}>Print Time: {printTime}</div>
         </div>
       </div>
    </div>
  );
}

export const PurchaseInvoicePrintPreviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const { settings, loading: settingsLoading } = useSettings();
  const [logoUrl, setLogoUrl] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 14;
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('Your Purchase Invoice');
  const [emailMessage, setEmailMessage] = useState('Please find your purchase invoice below.');
  const [sending, setSending] = useState(false);
  const [emailTo, setEmailTo] = useState(invoice?.customer?.email || '');

  useEffect(() => {
    if (id) {
      apiService
        .get(`/invoices/${id}`)
        .then(response => {
          setInvoice(response.data);
        })
        .catch(err => {
          console.error('Failed to fetch invoice:', err);
          setError('Failed to load invoice data.');
        });
    }
  }, [id]);
  
  useEffect(() => {
    if (settings?.company?.logo) {
      setLogoUrl(`${process.env.REACT_APP_API_URL || 'https://aircons-inventory-backend.onrender.com'}${settings.company.logo}`);
    } else {
      setLogoUrl('/The Aircons Ltd. Logo Org.png');
    }
  }, [settings]);

  const purchaseSettings = settings?.purchaseInvoice || {};
  const company = settings?.company || {};
  
  if (settingsLoading) {
    return <LoadingSpinner size="lg" />;
  }
  if (error) return <div className="text-center text-red-600 p-8">{error}</div>;
  if (!invoice) return <div className="text-center text-gray-500 p-8">Invoice not found.</div>;

  const handleSendEmail = async () => {
    setSending(true);
    try {
      await apiService.post(`/invoices/${invoice._id}/send-email`, {
        email: invoice.customer?.email || emailTo,
        subject: emailSubject,
        message: emailMessage
      });
      setShowEmailModal(false);
      setSending(false);
      alert('Email sent successfully!');
    } catch (e) {
      setSending(false);
      alert('Failed to send email.');
    }
  };

  const content = (
    <div className="bg-white flex flex-col items-start justify-start p-4 relative font-serif">
      <div className="w-full max-w-4xl bg-white shadow-lg rounded-lg p-4 print:p-0 print:shadow-none print:bg-white relative">
        <button
          onClick={() => navigate(-1)}
          className="print:hidden absolute left-4 top-0 -translate-y-full z-50 p-2 bg-gray-200 text-black rounded font-semibold text-base shadow border-none cursor-pointer flex items-center justify-center"
          aria-label="Close"
        >
          <X size={22} />
        </button>
        <button
          onClick={() => window.print()}
          className="print:hidden absolute right-4 top-0 -translate-y-full z-50 px-5 py-2 bg-blue-600 text-white rounded font-semibold text-base shadow border-none cursor-pointer flex items-center gap-2"
          aria-label="Print"
        >
          <Printer size={20} />
          Print
        </button>
        <button
          onClick={() => setShowEmailModal(true)}
          className="print:hidden absolute right-32 top-0 -translate-y-full z-50 px-5 py-2 bg-green-600 text-white rounded font-semibold text-base shadow border-none cursor-pointer flex items-center gap-2"
          aria-label="Email"
        >
          Email
        </button>
        <PurchaseInvoicePrintView
          invoice={invoice}
          settings={settings}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          printMode={true}
        />
      </div>
      <Modal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} title="Send Invoice Email" size="xl" className="!max-w-7xl min-h-[600px]">
        <div className="space-y-4">
          <div><b>To:</b> {invoice.customer?.email || ''}</div>
          {!invoice.customer?.email && (
            <Input label="Supplier Email" value={emailTo} onChange={e => setEmailTo(e.target.value)} />
          )}
          <Input label="Subject" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} />
          <Input label="Message" value={emailMessage} onChange={e => setEmailMessage(e.target.value)} />
          <div className="border rounded p-2 max-h-64 overflow-auto bg-gray-50">
            <div id="purchase-invoice-email-preview">
              <PurchaseInvoicePrintView
                invoice={invoice}
                settings={{}}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                printMode={true}
              />
            </div>
          </div>
          <button
            onClick={handleSendEmail}
            className="px-4 py-2 bg-green-600 text-white rounded font-semibold"
            disabled={sending || (!invoice.customer?.email && !emailTo)}
          >
            {sending ? 'Sending...' : 'Send Email'}
          </button>
        </div>
      </Modal>
    </div>
  );

  const printRoot = typeof window !== 'undefined' ? document.getElementById('print-root') : null;
  return printRoot ? ReactDOM.createPortal(content, printRoot) : content;
};

export default PurchaseInvoicePrintView; 
